/**
 * ClientSyncHandler — Real-time cross-device session sync via BATON WebSocket.
 *
 * Singleton handler (same pattern as ClientTransportHandler, ClientDiscoveryHandler).
 * Uses the existing BATON WebSocket to exchange `sync:*` messages with MaestroRoom DO.
 *
 * Controller/Observer model:
 *  - Each device has a stable deviceId (localStorage).
 *  - Every sync event carries controllerId — the device that last took a playback action.
 *  - Latest user interaction wins: any tap (play, pause, skip, volume, zone switch)
 *    immediately makes that device the controller.
 *  - Observers see real-time state updates.
 */

import { useMaestroStore } from '../store/maestroStore';
import { resolveRendererDisplayName, resolveZoneDisplayName } from '../utils/zoneDisplayName';
import { getCurrentPlaybackSnapshot, isRadioActive } from '../transport/PlaybackSnapshot';
import type { DisplayTrack } from '../transport/PlaybackSnapshot';
import { createCommand, PROTOCOL_VERSION } from '../protocols/BATON';
import type { BatonMessage, CommandPayload } from '../protocols/BATON';
import { rlog } from '../utils/remoteLogger';
import { TransportCommand } from '../transport/TransportCommand';
import { detectPlatform } from '../utils/platform';
import { diagnosticLog } from '../utils/diagnosticLogger';
import { pushQueueSnapshotToServer } from '../services/queueSyncPush';
import { applyInboundGaplessPref } from '../services/gaplessPlaybackSettings';

const SYNC_OBSERVER_DISPLAY_ONLY = true;
const PUSH_DEBOUNCE_MS = 300;
const POSITION_PUSH_INTERVAL_MS = 10_000;
const REMOTE_APPLY_GRACE_MS = 500;
const HANDOFF_GRACE_MS = 5_000;

export interface RemoteSession {
  controllerId: string;
  deviceName: string;
  rendererId: string | null;
  rendererName: string | null;
  track: DisplayTrack | null;
  playbackState: string;
  position: number;
  duration: number;
  activeSource: string;
  queueIndex: number;
  volume: number;
}

function getDeviceId(): string {
  try {
    const stored = localStorage.getItem('maestro_device_id');
    if (stored) return stored;
  } catch {}
  const prefix = detectPlatform().deviceIdPrefix;
  const id = `${prefix}_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 9)}`;
  try { localStorage.setItem('maestro_device_id', id); } catch {}
  return id;
}

function getDeviceName(): string {
  return detectPlatform().deviceName;
}

function mapQueueForSync(queue: any[]): any[] {
  return queue.slice(0, 200).map((item: any) => {
    const t = item?.track || item;
    const art = t.artworkUrl || t.artUrl || t.coverUrl || t.cover || t.imageUrl || t.artwork || '';
    const mapped: Record<string, any> = {
      id: t.id || '',
      title: t.title || '',
      artist: t.artist || '',
      album: t.album || '',
      artUrl: art,
      artworkUrl: art,
      albumId: t.albumId || t.album_key || t.albumKey || '',
      formatId: t.formatId || 27,
      qobuzTrackId: t.qobuzTrackId || t.serviceTrackId || '',
      duration: t.duration || 0,
      source: t.source || 'library',
    };
    if (t.source === 'local_library') {
      mapped.file_path = t.file_path || '';
      mapped.machine_id = t.machine_id || '';
      mapped.format = t.format || '';
      mapped.sample_rate = t.sample_rate || null;
      mapped.bit_depth = t.bit_depth || null;
      mapped.cover_art_url = t.cover_art_url || '';
    }
    return mapped;
  });
}

export class ClientSyncHandler {
  private ws: WebSocket | null = null;
  private _deviceId: string;
  private _deviceName: string;
  private _controllerId: string | null = null;
  private _remoteSession: RemoteSession | null = null;
  private _pushTimer: ReturnType<typeof setTimeout> | null = null;
  private _positionTimer: ReturnType<typeof setInterval> | null = null;
  private _storeUnsub: (() => void) | null = null;
  private _lastPushFingerprint = '';
  private _observerCallbacks = new Set<() => void>();
  private _progressTimer: ReturnType<typeof setInterval> | null = null;
  private _retryTimer: ReturnType<typeof setTimeout> | null = null;
  private _applyingRemoteState = false;
  private _remoteApplyUntil = 0;
  private _yieldedControlAt = 0;
  private _lastPlayingPushTs = 0;

  constructor() {
    this._deviceId = getDeviceId();
    this._deviceName = getDeviceName();
    this._controllerId = this._deviceId;
  }

  connect(ws: WebSocket): void {
    this.ws = ws;
    this._subscribeStores();
    this._requestState();
    this._startPositionPush();
    rlog('SYNC', 'connected', { deviceId: this._deviceId, deviceName: this._deviceName });
    const plat = detectPlatform();
    console.log('[BATON:register]', {
      deviceName: this._deviceName,
      deviceId: this._deviceId,
      platform: plat,
      href: typeof location !== 'undefined' ? location.href : '(n/a)',
      timestamp: new Date().toISOString(),
    });
  }

  disconnect(): void {
    if (this._storeUnsub) { this._storeUnsub(); this._storeUnsub = null; }
    if (this._pushTimer) { clearTimeout(this._pushTimer); this._pushTimer = null; }
    if (this._positionTimer) { clearInterval(this._positionTimer); this._positionTimer = null; }
    if (this._progressTimer) { clearInterval(this._progressTimer); this._progressTimer = null; }
    if (this._retryTimer) { clearTimeout(this._retryTimer); this._retryTimer = null; }
    this.ws = null;
    this._remoteSession = null;
    this._lastPushFingerprint = '';
    console.log('[SyncHandler] Disconnected');
  }

  claimControl(): void {
    if (this._controllerId === this._deviceId && !this._remoteSession) return;
    const wasObserving = this.isObserving();
    const prevController = this._controllerId;
    this._controllerId = this._deviceId;
    this._remoteSession = null;
    if (this._progressTimer) { clearInterval(this._progressTimer); this._progressTimer = null; }
    console.log('[SyncHandler] claimControl()', {
      wasObserving,
      prevController,
      stack: new Error().stack?.split('\n').slice(1, 5).join(' | '),
    });
    this._notifyObserverCallbacks();
    this._schedulePush();
  }

  /** Push an intentionally cleared queue to peers and QueueSession (bypasses empty-idle skip). */
  pushClearedQueue(): void {
    this.claimControl();
    if (this._pushTimer) {
      clearTimeout(this._pushTimer);
      this._pushTimer = null;
    }
    this._lastPushFingerprint = '';
    this._pushState({ allowEmpty: true });

    const snap = getCurrentPlaybackSnapshot();
    void pushQueueSnapshotToServer({
      playbackState: 'idle',
      activeSource: snap.activeSource,
      currentTrackId: null,
      currentTrack: undefined,
      queueIndex: 0,
      position: 0,
      duration: 0,
      volume: snap.volume,
      repeatMode: snap.repeatMode,
      shuffled: snap.shuffled,
      shuffleOrder: snap.shuffleOrder,
      rendererId: snap.renderer?.id || null,
      rendererName: snap.renderer?.name || null,
      queue: [],
      controllerId: this._deviceId,
    });
  }

  isObserving(): boolean {
    return this._remoteSession !== null && this._controllerId !== this._deviceId;
  }

  getRemoteSession(): RemoteSession | null {
    return this._remoteSession;
  }

  getDeviceId(): string {
    return this._deviceId;
  }

  onObserverStateChanged(cb: () => void): () => void {
    this._observerCallbacks.add(cb);
    return () => { this._observerCallbacks.delete(cb); };
  }

  private _notifyObserverCallbacks(): void {
    this._observerCallbacks.forEach((cb) => { try { cb(); } catch {} });
  }

  /**
   * When another device claims control and sends pause/stop, the local device
   * (which may have the Connector/Tauri with direct renderer access) must relay
   * the actual command to the hardware. The remote device may not have the renderer
   * in its native discovery — only the Connector host can reliably reach it.
   */
  private _relayTransportCommand(state: string): void {
    const store = useMaestroStore.getState();
    const renderer = store.activeConnectorRenderer;
    if (!renderer) return;

    const isTauri = !!(window as any).__TAURI__;
    if (!isTauri) return;

    console.log('[SyncHandler] Relaying', state, 'to renderer', renderer.name, 'via Connector');
    if (state === 'paused') {
      TransportCommand.pauseRenderer(renderer.id);
    } else if (state === 'stopped') {
      TransportCommand.stopRenderer(renderer.id);
    } else if (state === 'playing') {
      TransportCommand.play();
    }
  }

  /** Real-time gapless preference propagation to other signed-in devices. */
  broadcastGaplessPref(rendererId: string, enabled: boolean, updatedAt: number): void {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) return;
    const message = createCommand('transport' as any, {
      cmd: 'syncGaplessPref',
      rendererId,
      enabled,
      updatedAt,
      controllerId: this._deviceId,
      ts: Date.now(),
    } as CommandPayload);
    this.ws.send(JSON.stringify(message));
  }

  /**
   * Handle an incoming sync event from BATON WebSocket (routed by ClientTransportHandler).
   */
  handleSyncEvent(payload: Record<string, any>): void {
    const event = payload.event as string;

    if (event === 'peerJoined') {
      rlog('SYNC', 'event', { event, controllerId: payload.controllerId });
      console.log(`[SyncHandler] Received sync event: ${event}`);
      if (this._controllerId === this._deviceId) {
        console.log('[SyncHandler] Peer joined — resetting fingerprint and pushing full state');
        this._lastPushFingerprint = '';
        this._pushState();
      }
      return;
    }

    // currentState is display-only — NEVER dispatch TransportCommand.play/pause/stop/playUri
    // and never apply snapshot in a way that could flip playbackState / drive WebAudioRenderer.
    // QueueSession may still say paused on reconnect while PMG is playing.
    if (event === 'currentState') {
      rlog('SYNC', 'event', { event, controllerId: payload.controllerId });
      console.log('[SyncHandler] currentState (display-only, no transport commands)');
      this._applyDisplayOnly(payload);
      return;
    }

    if (event === 'gaplessPrefChanged') {
      const rendererId = String(payload.rendererId || '');
      const enabled = payload.enabled === true;
      const updatedAt = typeof payload.updatedAt === 'number' ? payload.updatedAt : 0;
      if (rendererId) {
        applyInboundGaplessPref(rendererId, enabled, updatedAt, 'baton');
      }
      return;
    }

    rlog('SYNC', 'event', { event, controllerId: payload.controllerId });
    console.log(`[SyncHandler] Received sync event: ${event}`);

    if (event === 'stateChanged') {
      try {
        const track = payload.track || payload.currentTrack;
        diagnosticLog(
          'info',
          'protocol',
          `BATON state update: ${event}`,
          {
            type: `sync:${event}`,
            playbackState: payload.playbackState ?? (payload.state as any)?.playbackState,
            trackId: track?.id,
            position: payload.position,
          },
        );
      } catch { /* ignore */ }
      void this._applyRemoteState(payload);
    }
  }

  /**
   * QueueSession / syncRequestState snapshot (event === 'currentState').
   * May update track / queue / position for an idle UI only.
   * Does NOT: change controllerId, call TransportCommand, or setPlaybackState.
   */
  private _applyDisplayOnly(payload: Record<string, any>): void {
    const store = useMaestroStore.getState();

    if (store.playbackState === 'playing') {
      const serverPos = typeof payload.position === 'number' ? payload.position : null;
      if (serverPos !== null && serverPos < store.position) {
        console.log('[SyncHandler] currentState: skipped — server position older than live local position during playback');
        return;
      }
    }

    const transportOrRendererActive =
      store.playbackState === 'loading'
      || store.rendererHasStream;
    if (transportOrRendererActive) {
      console.log('[SyncHandler] currentState: skipped — playback or renderer stream active (no store churn)');
      return;
    }

    // Local controller cleared the queue — don't resurrect a stale server snapshot.
    if (
      this._controllerId === this._deviceId
      && store.queue.length === 0
      && !store.currentTrack
      && store.playbackState === 'idle'
      && ((payload.queue?.length ?? 0) > 0 || payload.track || payload.currentTrack)
    ) {
      console.log('[SyncHandler] currentState: skipped — local controller cleared session');
      return;
    }

    const track = payload.track || payload.currentTrack || null;
    if (!track) return;

    this._applyingRemoteState = true;

    if (payload.position !== undefined) {
      store.updateTime(Math.floor(payload.position), payload.duration || track.duration || 0);
    }

    if (payload.queue && payload.queue.length > 0) {
      void this._applyValidatedQueue(payload.queue, payload.queueIndex || 0, track, true)
        .finally(() => {
          this._applyingRemoteState = false;
          console.log('[SyncHandler] Display-only state applied from server:', track.title || '(no title)');
        });
      return;
    }

    const artUrl = track.artUrl || track.artworkUrl || track.coverUrl || '';
    store.setCurrentTrack({ ...track, artworkUrl: artUrl, artUrl } as any);

    this._applyingRemoteState = false;
    console.log('[SyncHandler] Display-only state applied from server:', track.title || '(no title)');
  }

  private async _applyValidatedQueue(
    queueTracks: any[],
    queueIndex: number,
    currentTrack: any | null,
    displayOnly = false,
  ): Promise<void> {
    const { sanitizeQueueTracks, shouldValidateLocalLibraryRefs } = await import('../services/localLibraryRefValidator');
    let queueItems = queueTracks.map((t: any) => ({ track: t }));
    let idx = queueIndex;
    let track = currentTrack;

    if (shouldValidateLocalLibraryRefs()) {
      const sanitized = await sanitizeQueueTracks(queueItems, idx, track);
      queueItems = sanitized.queue;
      idx = sanitized.queueIndex;
      track = sanitized.currentTrack;
    }

    const store = useMaestroStore.getState();
    store.setQueue(queueItems, idx);

    if (track) {
      const artUrl = track.artUrl || track.artworkUrl || track.coverUrl || '';
      store.setCurrentTrack({ ...track, artworkUrl: artUrl, artUrl, coverUrl: artUrl } as any);
    } else if (!displayOnly) {
      store.setCurrentTrack(null);
    }
  }

  // ═══════════════════════════════════════════════════════════════
  // Internal: Request state on connect
  // ═══════════════════════════════════════════════════════════════

  private _requestState(): void {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) return;

    // Always request state from server first. Don't auto-claim controller
    // based on store.playbackState — the BATON handshake or a previous session
    // may have set it to 'playing' even though this device isn't genuinely
    // driving playback. The peerJoined mechanism handles announcement: when a
    // new peer connects, the real controller pushes state immediately.
    const message = createCommand('transport' as any, {
      cmd: 'syncRequestState',
    } as CommandPayload);
    this.ws.send(JSON.stringify(message));
    console.log('[SyncHandler] Requesting current state from server');

    if (this._retryTimer) clearTimeout(this._retryTimer);
    this._retryTimer = setTimeout(() => {
      this._retryTimer = null;
      if (!this._remoteSession && this.ws && this.ws.readyState === WebSocket.OPEN) {
        // No remote state received — likely the only device.
        // Push state so QueueSession is populated for future peers.
        this._controllerId = this._deviceId;
        this._lastPushFingerprint = '';
        this._pushState();
        console.log('[SyncHandler] No remote state received — claiming controller');
      }
    }, 1500);
  }

  // ═══════════════════════════════════════════════════════════════
  // Internal: Subscribe to stores and push changes
  // ═══════════════════════════════════════════════════════════════

  private _subscribeStores(): void {
    if (this._storeUnsub) this._storeUnsub();

    this._storeUnsub = useMaestroStore.subscribe((state, prev) => {
      if (this._applyingRemoteState || Date.now() < this._remoteApplyUntil) return;

      const trackChanged = state.currentTrack?.id !== prev?.currentTrack?.id;
      const stateChanged = state.playbackState !== prev?.playbackState;
      const queueChanged = state.queue.length !== prev?.queue.length || state.queueIndex !== prev?.queueIndex;
      const sourceChanged = state.activeSource !== prev?.activeSource;
      const rendererChanged = state.activeConnectorRenderer?.id !== prev?.activeConnectorRenderer?.id;

      if (!(trackChanged || stateChanged || queueChanged || sourceChanged || rendererChanged)) return;

      if (this.isObserving()) {
        const localActing = state.playbackState === 'playing' || state.playbackState === 'loading';
        if (localActing || trackChanged || queueChanged) {
          console.log('[SyncHandler] Local action while observing — claiming control', {
            localActing,
            trackChanged,
            queueChanged,
            playbackState: state.playbackState,
            prevPlaybackState: prev?.playbackState,
          });
          this.claimControl();
        } else {
          console.log('[SyncHandler] Store change while observing — suppressed', {
            stateChanged,
            playbackState: state.playbackState,
            prevPlaybackState: prev?.playbackState,
          });
          return;
        }
      }

      this._schedulePush();
    });
  }

  private _schedulePush(): void {
    if (this._pushTimer) clearTimeout(this._pushTimer);
    this._pushTimer = setTimeout(() => this._pushState(), PUSH_DEBOUNCE_MS);
  }

  private _pushState(options?: { allowEmpty?: boolean }): void {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) return;
    if (this.isObserving()) {
      console.log('[SyncHandler] _pushState blocked — isObserving=true', {
        controllerId: this._controllerId,
        deviceId: this._deviceId,
        hasRemoteSession: !!this._remoteSession,
      });
      return;
    }

    const snap = getCurrentPlaybackSnapshot();
    if (
      !options?.allowEmpty
      && snap.playbackState === 'idle'
      && snap.queue.length === 0
      && !snap.isRadio
    ) return;

    const fingerprint = `${snap.activeSource}|${snap.track?.id || ''}|${snap.queueIndex}|${snap.playbackState}|${snap.queue.length}|${snap.renderer?.id || 'local'}`;
    if (fingerprint === this._lastPushFingerprint) return;
    this._lastPushFingerprint = fingerprint;

    const ms = useMaestroStore.getState();
    const rawTrack = ms.currentTrack;
    const artworkUrl = rawTrack?.artworkUrl || snap.track?.artUrl || '';
    const enrichedTrack = snap.track ? {
      ...snap.track,
      artworkUrl,
      artUrl: artworkUrl || snap.track.artUrl,
      coverUrl: artworkUrl,
      albumId: snap.track.albumId || rawTrack?.albumId || '',
    } : null;

    const payload: Record<string, any> = {
      cmd: 'syncState',
      controllerId: this._deviceId,
      deviceName: this._deviceName,
      track: enrichedTrack,
      playbackState: snap.playbackState,
      position: snap.position,
      duration: snap.duration,
      rendererId: snap.renderer?.id || null,
      rendererName: snap.renderer?.name || null,
      activeSource: snap.activeSource,
      queueIndex: snap.queueIndex,
      queue: mapQueueForSync(snap.queue),
      volume: snap.volume,
      repeatMode: snap.repeatMode,
      shuffled: snap.shuffled,
      ts: Date.now(),
    };

    if (snap.playbackState === 'playing') {
      this._lastPlayingPushTs = Date.now();
    }

    const message = createCommand('transport' as any, payload as CommandPayload);
    this.ws.send(JSON.stringify(message));
    console.log('[SyncHandler] Pushed state:', snap.track?.title || '(no track)', '| state:', snap.playbackState);
  }

  private _startPositionPush(): void {
    if (this._positionTimer) clearInterval(this._positionTimer);
    let _wsHealthCounter = 0;
    this._positionTimer = setInterval(() => {
      // Diagnostic: log WebSocket readyState every ~5 intervals (50s)
      _wsHealthCounter++;
      if (_wsHealthCounter % 5 === 0) {
        const wsState = this.ws ? this.ws.readyState : -1;
        const stateNames = ['CONNECTING', 'OPEN', 'CLOSING', 'CLOSED'];
        console.log('[SyncHandler:WS] readyState:', stateNames[wsState] || `unknown(${wsState})`);
      }

      if (!this.ws || this.ws.readyState !== WebSocket.OPEN) return;
      if (this.isObserving()) return;

      const snap = getCurrentPlaybackSnapshot();
      if (snap.playbackState !== 'playing') return;

      const payload: Record<string, any> = {
        cmd: 'syncState',
        controllerId: this._deviceId,
        deviceName: this._deviceName,
        position: snap.position,
        duration: snap.duration,
        playbackState: 'playing',
        activeSource: snap.activeSource,
        ts: Date.now(),
      };

      const message = createCommand('transport' as any, payload as CommandPayload);
      this.ws.send(JSON.stringify(message));
    }, POSITION_PUSH_INTERVAL_MS);
  }

  // ═══════════════════════════════════════════════════════════════
  // Internal: Apply remote state from controller
  // ═══════════════════════════════════════════════════════════════

  private async _applyRemoteState(payload: Record<string, any>): Promise<void> {
    const remoteControllerId = payload.controllerId as string;

    if (remoteControllerId === this._deviceId) return;

    const store = useMaestroStore.getState();
    const localIsPlaying = store.playbackState === 'playing' || store.playbackState === 'loading';
    const wasLocalController = this._controllerId === this._deviceId;
    const isPositionOnly = !payload.track && !payload.currentTrack && !payload.queue;
    const msSinceLastPlayingPush = Date.now() - this._lastPlayingPushTs;

    console.log('[SyncHandler] _applyRemoteState entry:', {
      incomingState: payload.playbackState,
      localState: store.playbackState,
      msSinceLastPlayingPush,
      fromController: remoteControllerId,
      fromDeviceName: payload.deviceName || '(none)',
      wasLocalController,
      isPositionOnly,
    });

    // Only ignore position-only updates when we're actively controlling playback.
    // Full state updates from another controller represent a control takeover
    // (e.g., observer tapped play/pause) — we must yield.
    if (localIsPlaying && wasLocalController && isPositionOnly) {
      return;
    }

    // ── Handoff grace: reject stale "paused" echoes ──
    // When this device just pushed "playing" and another device echoes
    // back "paused" within the grace window, it's a stale echo — not a
    // genuine control takeover. The Desktop Browser frequently sends its
    // old "paused" state in response to peerJoined or store subscription
    // churn. Without this guard the echo overwrites Zustand, freezing the
    // progress bar while the renderer keeps playing.
    const incomingState = payload.playbackState || 'stopped';
    const withinGrace = this._lastPlayingPushTs > 0 && msSinceLastPlayingPush < HANDOFF_GRACE_MS;
    const isPauseEcho = (incomingState === 'paused' || incomingState === 'stopped')
      && localIsPlaying && withinGrace;

    if (isPauseEcho) {
      console.log('[SyncHandler] Rejecting stale paused echo within handoff grace', {
        fromController: remoteControllerId,
        msSinceLocalPlay: msSinceLastPlayingPush,
      });
      return;
    }

    if (wasLocalController) {
      this._yieldedControlAt = Date.now();
      console.log('[SyncHandler] Yielding control to', remoteControllerId, '— becoming observer');
    }
    this._controllerId = remoteControllerId;

    const track = payload.track || payload.currentTrack || null;

    if (isPositionOnly) {
      if (this._remoteSession) {
        this._remoteSession.position = payload.position ?? this._remoteSession.position;
        this._remoteSession.duration = payload.duration ?? this._remoteSession.duration;
        if (payload.playbackState) this._remoteSession.playbackState = payload.playbackState;
      }

      this._applyingRemoteState = true;
      if (payload.position !== undefined) {
        store.updateTime(
          Math.floor(payload.position),
          payload.duration || store.duration || 0,
        );
      }
      this._applyingRemoteState = false;
      this._remoteApplyUntil = Date.now() + REMOTE_APPLY_GRACE_MS;
      this._notifyObserverCallbacks();
      return;
    }

    // Full state update: track, queue, renderer, volume, etc.
    const artUrl = track
      ? (track.artUrl || track.artworkUrl || track.coverUrl || track.cover || track.imageUrl || '')
      : '';

    const rendererId = payload.rendererId || null;
    const rendererName = payload.rendererName || null;
    let playbackState = incomingState;

    // ── Renderer update FIRST ──
    // Process renderer before playbackState guards so that rendererHasStream
    // is accurate when the guards check it (setActiveConnectorRenderer can
    // clear rendererHasStream if the ID changes).
    if (rendererId) {
      const skipDesktopDacOnMobile =
        !detectPlatform().isTauriDesktop
        && (rendererId.startsWith('local-native:') || rendererId === 'local-native');
      if (skipDesktopDacOnMobile) {
        console.log(
          '[SyncHandler] Ignoring BATON local-native renderer on non-Tauri — cannot drive desktop DAC:',
          rendererId,
        );
      } else {
      const localRenderer = store.activeConnectorRenderer;
      if (localRenderer && store.rendererHasStream && localRenderer.id !== rendererId) {
        console.log('[SyncHandler] Preserving local renderer', localRenderer.id, '(has active stream, ignoring remote', rendererId, ')');
      } else {
        try {
          localStorage.setItem('maestro_connector_active_zone', rendererId);
          localStorage.setItem('maestro_ios_active_zone', rendererId);
        } catch {}

        const zones = store.discovery?.discoveredZones || [];
        const match = zones.find((z: any) => z.id === rendererId)
          || (rendererName
            ? zones.find((z: any) =>
                z.name?.toLowerCase() === rendererName.toLowerCase()
                && z.id !== 'local_computer')
            : null);
        const zoneId = match?.id || rendererId;
        const zoneName = match
          ? resolveZoneDisplayName(match, { discoveredZones: zones, selectedUsbDac: store.selectedUsbDac })
          : resolveRendererDisplayName(
            { id: zoneId, name: rendererName || undefined },
            { discoveredZones: zones, selectedUsbDac: store.selectedUsbDac },
          );

        store.setActiveConnectorRenderer({ id: zoneId, name: zoneName });
        if (match) {
          store.setActiveZone(match);
        } else {
          store.setActiveZone({ id: zoneId, name: zoneName, type: 'upnp' } as any);
        }
      }
      }
    }

    // ── Guard: protect local playback from stale observer echoes ──
    // Secondary guard for cases that pass the handoff grace (e.g., grace
    // expired but local is still playing and no genuine takeover occurred).
    const localPlayingNow = store.playbackState === 'playing' || store.playbackState === 'loading';
    const controlTakeover = wasLocalController && remoteControllerId !== this._deviceId;

    if ((playbackState === 'paused' || playbackState === 'stopped') && localPlayingNow && !controlTakeover) {
      console.log('[SyncHandler] Ignoring remote', playbackState, '— local device is playing (not a control takeover)');
      playbackState = store.playbackState;
    }

    // Relay transport commands to the renderer when a remote device controls playback.
    // This covers both initial takeover and ongoing remote control (observer with Connector).
    const shouldRelay = controlTakeover || !wasLocalController;
    if (shouldRelay && playbackState !== store.playbackState) {
      if (SYNC_OBSERVER_DISPLAY_ONLY && remoteControllerId !== this._deviceId) {
        console.warn('[SyncHandler] Observer mode: suppressing transport relay', {
          from: remoteControllerId,
          renderer: store.activeConnectorRenderer?.name || '(none)',
          command: playbackState,
        });
      } else {
        console.log('[SyncHandler] Relaying remote', playbackState, 'from', remoteControllerId);
        this._relayTransportCommand(playbackState);
      }
    }

    this._remoteSession = {
      controllerId: remoteControllerId,
      deviceName: payload.deviceName || payload.controllerDeviceName || 'Another device',
      rendererId,
      rendererName,
      track,
      playbackState,
      position: payload.position || 0,
      duration: payload.duration || 0,
      activeSource: payload.activeSource || 'library',
      queueIndex: payload.queueIndex || 0,
      volume: payload.volume ?? store.volume,
    };

    console.log('[SyncHandler] Applying remote state from', this._remoteSession.deviceName, ':', track?.title || '(no track)', '| art:', artUrl ? 'yes' : 'MISSING', '| state:', playbackState);

    this._applyingRemoteState = true;

    if (track) {
      store.setCurrentTrack({
        ...track,
        artworkUrl: artUrl,
        artUrl: artUrl,
        coverUrl: artUrl,
      } as any);
    }

    if (payload.position !== undefined) {
      store.updateTime(
        Math.floor(payload.position),
        payload.duration || track?.duration || 0,
      );
    }

    if (payload.volume !== undefined) {
      store.setVolume(payload.volume);
    }

    const serverSource = payload.activeSource || 'library';
    if (serverSource !== 'radio' && payload.queue && payload.queue.length > 0) {
      await this._applyValidatedQueue(payload.queue, payload.queueIndex || 0, track, false);
    }

    // Apply playbackState — the guards above already filtered transient pauses,
    // so whatever value is in `playbackState` here is safe to write.
    if (playbackState !== store.playbackState) {
      store.setPlaybackState(playbackState);
    }

    this._applyingRemoteState = false;
    this._remoteApplyUntil = Date.now() + REMOTE_APPLY_GRACE_MS;

    this._notifyObserverCallbacks();

    // Clear any existing progress timer before starting a new one
    if (this._progressTimer) {
      clearInterval(this._progressTimer);
      this._progressTimer = null;
    }

    if (playbackState === 'playing') {
      const startTime = Date.now();
      const basePosition = Math.floor(payload.position || 0);
      const dur = payload.duration || track?.duration || 0;
      this._progressTimer = setInterval(() => {
        if (this._controllerId === this._deviceId) {
          if (this._progressTimer) { clearInterval(this._progressTimer); this._progressTimer = null; }
          return;
        }
        const s = useMaestroStore.getState();
        if (s.playbackState === 'playing' || s.playbackState === 'loading') {
          if (this._progressTimer) { clearInterval(this._progressTimer); this._progressTimer = null; }
          return;
        }
        const elapsed = (Date.now() - startTime) / 1000;
        const pos = Math.min(basePosition + elapsed, dur);
        s.updateTime(pos, dur);
      }, 1000);
    }

    this._pushIOSNowPlaying(track, playbackState, payload.position || 0, payload.duration || track?.duration || 0);
  }

  private _pushIOSNowPlaying(
    track: DisplayTrack | null,
    playbackState: string,
    position: number,
    duration: number,
  ): void {
    try {
      const native = (window as any).__maestroNative;
      if (!native) return;

      const postMsg = (type: string, data: Record<string, unknown>) => {
        if (native.postMessage) { native.postMessage(type, data); return; }
        const wk = (window as any).webkit?.messageHandlers?.maestroNative;
        if (wk?.postMessage) wk.postMessage({ action: type, data });
      };

      if (track) {
        postMsg('updateNowPlaying', {
          title: track.title || 'Unknown Track',
          artist: track.artist || 'Unknown Artist',
          album: track.album || '',
          artworkURL: track.artUrl || '',
          duration: duration > 10000 ? duration / 1000 : duration,
          elapsed: position,
          playbackRate: playbackState === 'playing' ? 1.0 : 0.0,
        });
      }
    } catch {}
  }
}

// ═══════════════════════════════════════════════════════════════
// SINGLETON
// ═══════════════════════════════════════════════════════════════

let _instance: ClientSyncHandler | null = null;

export function getSyncHandler(): ClientSyncHandler {
  if (!_instance) {
    _instance = new ClientSyncHandler();
  }
  return _instance;
}

export function resetSyncHandler(): void {
  if (_instance) {
    _instance.disconnect();
    _instance = null;
  }
}

// Debug utilities — callable from browser console via window.__batonDebug
if (typeof window !== 'undefined') {
  (window as any).__batonDebug = {
    async listSessions() {
      try {
        const auth = JSON.parse(localStorage.getItem('maestro_auth') || '{}');
        const token = auth?.session?.access_token || auth?.access_token;
        if (!token) { console.error('No auth token found'); return; }
        const resp = await fetch('https://api.maestroaudio.net/api/admin/baton-sessions', {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await resp.json();
        console.table(data.clients);
        return data;
      } catch (e) { console.error('Failed:', e); }
    },
    async kickSession(clientId: string) {
      try {
        const auth = JSON.parse(localStorage.getItem('maestro_auth') || '{}');
        const token = auth?.session?.access_token || auth?.access_token;
        if (!token) { console.error('No auth token found'); return; }
        const resp = await fetch(`https://api.maestroaudio.net/api/admin/baton-sessions/kick/${clientId}`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await resp.json();
        console.log('Kick result:', data);
        return data;
      } catch (e) { console.error('Failed:', e); }
    },
  };
}
